import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Play, Filter, RefreshCcw, ExternalLink } from "lucide-react";
import PageHeader from "../components/PageHeader";
import CompanyPecBanner from "../components/CompanyPecBanner";
import DataTable from "../components/DataTable";
import LoadingState from "../components/LoadingState";
import TechnicalStatusBadge from "../components/TechnicalStatusBadge";
import ReviewStatusBadge from "../components/ReviewStatusBadge";
import { Button } from "../components/ui/button";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import api from "../lib/api";
import { downloadAuthenticated } from "../lib/download";
import { useAuth } from "../lib/auth";

function formatDT(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const yyyy = d.getFullYear();
        const hh = String(d.getHours()).padStart(2, "0");
        const mi = String(d.getMinutes()).padStart(2, "0");
        return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
    } catch { return iso; }
}

export default function PecAnalysesPage() {
    const navigate = useNavigate();
    const { canAnalyze, companyId } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("__any__");

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append("limit", "200");
            if (statusFilter && statusFilter !== "__any__") params.append("technical_status", statusFilter);
            const { data } = await api.get(`/pec/?${params.toString()}`);
            setItems(data?.items || []);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, statusFilter]);

    const columns = [
        {
            key: "created_at", header: "Data", sortable: true, render: (r) => (
                <span className="text-xs text-[#64748B]">{formatDT(r.created_at)}</span>
            )
        },
        {
            key: "subject", header: "Oggetto", sortable: true, render: (r) => (
                <div>
                    <div className="text-sm font-medium text-[#172033] max-w-md truncate">{r.subject || "(senza oggetto)"}</div>
                    <div className="text-xs text-[#64748B]">{r.from_address || "—"}</div>
                </div>
            )
        },
        { key: "company_name", header: "Azienda", render: (r) => <span className="text-xs">{r.company_name || "—"}</span> },
        {
            key: "technical_status", header: "Autenticità tecnica", sortable: true, render: (r) => (
                <div className="flex flex-col gap-1">
                    <TechnicalStatusBadge status={r.technical_status || "Non verificabile"} />
                    <span className="text-[10px] text-[#64748B]">Score: {r.technical_score ?? "—"}/100</span>
                </div>
            )
        },
        {
            key: "sender_trust", header: "Affidabilità mittente", render: (r) => (
                r.sender_reliability != null ? (
                    <span className="text-xs font-mono text-[#172033]" data-testid={`sender-score-${r.id}`}>
                        {r.sender_reliability}/100
                    </span>
                ) : (
                    <span className="text-xs text-[#94A3B8]" title="Non ancora calcolato">—</span>
                )
            )
        },
        {
            key: "content_risk", header: "Rischio contenuto", render: (r) => {
                if (r.content_risk_status === "ai_disabled") {
                    return (
                        <span
                            className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#0891B2] text-white"
                            data-testid={`content-ai-off-${r.id}`}
                            title="AI disabilitata per l'azienda"
                        >
                            AI OFF
                        </span>
                    );
                }
                if (r.content_risk_status === "error") {
                    return <span className="text-xs text-[#DC2626] font-semibold" title="Errore durante l'analisi">Errore</span>;
                }
                if (r.content_risk != null) {
                    return (
                        <span className="text-xs font-mono text-[#172033]" data-testid={`content-score-${r.id}`}>
                            {r.content_risk}/100
                        </span>
                    );
                }
                return <span className="text-xs text-[#94A3B8]" title="Non ancora calcolato">—</span>;
            }
        },
        { key: "review_status", header: "Stato revisione", render: (r) => <ReviewStatusBadge status={
            r.review_status === "nuova" ? "Nuova" :
                r.review_status === "in_revisione" ? "In revisione" :
                    r.review_status === "chiusa" ? "Chiusa" : "Nuova"} /> },
        {
            key: "actions", header: "", render: (r) => (
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(`/pec/analyses/${r.id}`)}
                    data-testid={`open-pec-${r.id}`}
                    className="gap-1"
                >
                    Apri
                    <ExternalLink className="w-3.5 h-3.5" />
                </Button>
            ),
            className: "text-right",
        },
    ];

    return (
        <div className="space-y-6">
            <PageHeader
                title="Analisi PEC"
                subtitle="Storico completo delle PEC analizzate."
                breadcrumbs={[{ label: "PEC" }, { label: "Analisi" }]}
                actions={
                    canAnalyze() && (
                        <div className="flex gap-2">
                            <Button variant="outline" onClick={load} data-testid="analyses-refresh-btn" className="gap-1">
                                <RefreshCcw className="w-4 h-4" />
                            </Button>
                            <Button
                                variant="outline"
                                data-testid="analyses-export-csv"
                                onClick={() => downloadAuthenticated("/pec/export/csv", "pec_analisi.csv")}
                                className="gap-1"
                            >
                                Esporta CSV
                            </Button>
                            <Button
                                onClick={() => navigate("/pec/upload")}
                                data-testid="analyses-new-btn"
                                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white gap-2"
                            >
                                <Play className="w-4 h-4" />
                                Analizza nuova PEC
                            </Button>
                        </div>
                    )
                }
            />
            <CompanyPecBanner />

            <div className="bg-white border border-[#DCE3EE] rounded-xl p-4 flex items-end gap-3">
                <div className="w-64">
                    <label className="text-xs font-medium text-[#64748B]">Filtra per stato tecnico</label>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="mt-1 h-9" data-testid="analyses-status-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                            <SelectItem value="__any__">Tutti</SelectItem>
                            <SelectItem value="PEC verificata">PEC verificata</SelectItem>
                            <SelectItem value="PEC verificata con avvertenze">Con avvertenze</SelectItem>
                            <SelectItem value="PEC anomala">PEC anomala</SelectItem>
                            <SelectItem value="Firma non valida">Firma non valida</SelectItem>
                            <SelectItem value="Certificato scaduto">Certificato scaduto</SelectItem>
                            <SelectItem value="Busta di trasporto mancante">Busta mancante</SelectItem>
                            <SelectItem value="Busta di anomalia rilevata">Busta di anomalia</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {loading ? (
                <LoadingState />
            ) : (
                <DataTable
                    testId="analyses-table"
                    columns={columns}
                    data={items}
                    pageSize={15}
                    emptyTitle="Nessuna PEC ancora analizzata"
                    emptyDescription="Usa il pulsante 'Analizza nuova PEC' per caricare la prima."
                    getRowKey={(r) => r.id}
                />
            )}
        </div>
    );
}
